import * as React from 'react';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import { SxProps, useTheme } from '@mui/material/styles';
import Popper from '@mui/material/Popper';

export interface VirtualizedMultiSelectProps<T> {
  options: T[];
  value: T[];
  onChange: (value: T[]) => void;
  styles?: SxProps;
  error?: boolean;
  errorStyles?: SxProps;
  loading?: boolean;
  loadingText?: string;
  label?: string;
  getOptionLabel: (option: T) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  dataTestId?: string;
}

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING
    }
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});
OuterElementType.displayName = 'OuterElementType';

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const theme = useTheme();
    const itemCount = itemData.length;
    const itemSize = 48;
    const getChildSize = () => itemSize;
    const getHeight = () => (itemCount > 8 ? 8 * itemSize : itemCount * itemSize);
    const gridRef = useResetCache(itemCount);

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            itemData={itemData}
            height={getHeight() + 2 * LISTBOX_PADDING}
            width="100%"
            ref={gridRef}
            outerElementType={OuterElementType}
            innerElementType="ul"
            itemSize={getChildSize}
            overscanCount={5}
            itemCount={itemCount}
          >
            {renderRow}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  }
);

const CustomPopper = (props: any) => <Popper {...props} placement="top-start" />;

export function VirtualizedMultiSelect<T>({
  options,
  value,
  onChange,
  styles,
  error,
  errorStyles,
  loading,
  loadingText,
  label,
  getOptionLabel,
  isOptionEqualToValue,
  dataTestId = 'virtualized-multi-select'
}: VirtualizedMultiSelectProps<T>) {
  return (
    <Box sx={{ position: 'relative' }}>
      <Autocomplete
        id={dataTestId}
        data-testid={dataTestId}
        sx={styles}
        multiple
        disableListWrap
        disablePortal
        value={value}
        slotProps={{
          listbox: {
            component: ListboxComponent
          },
          popper: {
            component: CustomPopper
          }
        }}
        options={options}
        loading={loading}
        loadingText={loadingText}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        onChange={(_event, newValue) => onChange(newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            slotProps={{ inputLabel: { shrink: true } }}
            error={error}
          />
        )}
      />
      {error && <Box sx={errorStyles}>This field is required</Box>}
    </Box>
  );
}
